import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, Trash2, Vote, CheckCircle2, Users, Clock, X, Save } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PollsPage = ({ getAuthHeaders, currentUser }) => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', options: ['', ''], anonymous: false });
  const isMgmt = ['admin','executive','superadmin','manager'].includes(currentUser?.role);

  useEffect(() => { fetch(); }, []);
  const fetch = async () => { try { const r = await axios.get(`${BACKEND_URL}/api/engagement/polls`, { headers: getAuthHeaders() }); setPolls(r.data); } catch(e) { toast.error('Failed'); } finally { setLoading(false); } };
  const create = async () => {
    const opts = form.options.filter(o => o.trim());
    if (!form.title || opts.length < 2) { toast.error('Need title and 2+ options'); return; }
    try { await axios.post(`${BACKEND_URL}/api/engagement/polls`, { ...form, options: opts }, { headers: getAuthHeaders() }); toast.success('Poll created'); setShowForm(false); setForm({ title: '', description: '', options: ['', ''], anonymous: false }); fetch(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const vote = async (pollId, option) => { try { await axios.post(`${BACKEND_URL}/api/engagement/polls/${pollId}/vote-option`, { option }, { headers: getAuthHeaders() }); toast.success('Vote recorded'); fetch(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const del = async (id) => { if(!window.confirm('Delete?')) return; try { await axios.delete(`${BACKEND_URL}/api/engagement/polls/${id}`, { headers: getAuthHeaders() }); toast.success('Deleted'); fetch(); } catch(e) { toast.error('Failed'); } };

  return (
    <div className="space-y-6" data-testid="polls-page">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-violet-950/30 to-slate-900 border border-violet-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center border border-violet-500/20"><BarChart3 className="w-7 h-7 text-violet-400" /></div>
            <div><h1 className="text-2xl font-bold text-white">Polls & Surveys</h1><p className="text-slate-400 text-sm">Vote and share your opinion</p></div>
          </div>
          {isMgmt && <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Plus className="w-4 h-4 mr-2" />Create Poll</Button>}
        </div>
      </div>

      {loading ? <div className="text-center py-16 text-slate-500">Loading...</div> : polls.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-slate-800/50 rounded-2xl"><BarChart3 className="w-12 h-12 text-slate-700 mx-auto mb-3" /><p className="text-slate-500">No polls yet</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {polls.map(poll => {
            const maxVotes = Math.max(...Object.values(poll.results || {}), 1);
            return (
              <div key={poll.id} className="bg-slate-900/60 border border-slate-800/50 rounded-2xl p-5 hover:border-violet-500/30 transition-all">
                <div className="flex items-start justify-between mb-3">
                  <div><h3 className="text-white font-semibold">{poll.title}</h3>{poll.description && <p className="text-slate-500 text-sm mt-1">{poll.description}</p>}</div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-violet-500/20 text-violet-400">{poll.total_votes} votes</Badge>
                    {isMgmt && <button onClick={() => del(poll.id)} className="p-1 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
                <div className="space-y-2">
                  {poll.options.map(opt => {
                    const count = poll.results?.[opt] || 0;
                    const pct = poll.total_votes > 0 ? Math.round((count / poll.total_votes) * 100) : 0;
                    return (
                      <button key={opt} onClick={() => !poll.is_closed && vote(poll.id, opt)} disabled={poll.is_closed}
                        className={`w-full text-left p-3 rounded-xl border transition-all relative overflow-hidden group ${poll.user_voted ? 'border-slate-700/50 cursor-default' : 'border-slate-700/50 hover:border-violet-500/30 cursor-pointer'}`}>
                        <div className="absolute inset-0 bg-violet-500/10 rounded-xl transition-all" style={{ width: `${pct}%` }} />
                        <div className="relative flex items-center justify-between">
                          <span className="text-white text-sm">{opt}</span>
                          <span className="text-violet-400 text-sm font-mono">{pct}%</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-600">
                  <span>By {poll.author}</span><span>{new Date(poll.created_at).toLocaleDateString('en-GB')}</span>
                  {poll.user_voted && <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Voted</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="bg-slate-950 border-violet-500/20 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-violet-400">Create Poll</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Question" className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:border-violet-500/50" />
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} placeholder="Description (optional)" className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none resize-none" />
            <div className="space-y-2">
              <label className="text-xs text-slate-400 uppercase tracking-wider">Options</label>
              {form.options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input value={opt} onChange={e => { const o = [...form.options]; o[i] = e.target.value; setForm({...form, options: o}); }} placeholder={`Option ${i+1}`} className="flex-1 px-4 py-2.5 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none" />
                  {form.options.length > 2 && <button onClick={() => setForm({...form, options: form.options.filter((_,j) => j !== i)})} className="p-2 text-red-400"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <button onClick={() => setForm({...form, options: [...form.options, '']})} className="text-violet-400 text-sm hover:text-violet-300">+ Add option</button>
            </div>
            <div className="flex justify-end gap-3"><Button onClick={() => setShowForm(false)} variant="outline" className="border-slate-700 text-slate-400">Cancel</Button><Button onClick={create} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white">Create Poll</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PollsPage;
